import { uniq } from "es-toolkit";
import { useCallback, useMemo } from "react";

import { FieldTags, FormRowField, FormikForm, optionize, useSearch } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { InteractModuleProperties } from "@skilitics/nautilus-endpoint-definition";

import { FormBody, Header, Heading, PageContainer, StyledForm, SubHeading } from "../../shared";
import { useFilterSettings } from "../FilterSettingsContext";

type FormValues = {
  tags: string[];
};

export const TagsPage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const { source } = useSearch<InteractModuleProperties>();

  const { tags, setTags } = useFilterSettings();

  const suggestions = useMemo(() => optionize(uniq(source.flatMap((module) => module.tags))), [source]);

  const initialValues: FormValues = useMemo(() => ({ tags }), [tags]);

  const handleSubmit = useCallback((values: FormValues) => setTags(values.tags), [setTags]);

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
        <SubHeading>{formatMessage("subHeading")}</SubHeading>
      </Header>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formatStrings}
        dataTest="tags-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="tags">
              <FieldTags suggestions={suggestions} />
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "Module Filtering",
  },
  subHeading: {
    en: "Tags Filter",
  },
  tagsLabel: {
    en: "Which tags would you like to filter by?",
  },
} satisfies FormatMessages;
